/**
 * useAIWorkspace Hook
 * Manages AI Workspace state with INDEPENDENT translate and generate states
 * 
 * UPDATES:
 * - Workspace toggle (open/close independently)
 * - Reset dropdown after execution
 * - Clear selection after generate
 * - ✅ INTEGRAÇÃO COM HISTÓRICO - Salva automaticamente os resultados
 */
import { useState, useCallback, useEffect } from 'react';
import {
  MAX_LANGUAGES,
  MAX_SELECTED_MESSAGES,
  MAX_REGENERATIONS,
  ACTION_TYPES,
  BLOCKED_MESSAGE_TYPES,
} from '../constants';
import savedMessagesService from '../../../services/global/savedMessagesService';

// ============================================
// STORAGE KEY para histórico
// ============================================
const HISTORY_STORAGE_KEY = 'ai_workspace_history';
const MAX_HISTORY_ITEMS = 50;

const useAIWorkspace = () => {
  // ============================================
  // WORKSPACE VISIBILITY (toggle bar)
  // ============================================
  const [isWorkspaceOpen, setIsWorkspaceOpen] = useState(false);

  // ============================================
  // SELECTION STATE
  // ============================================
  const [selectedMessages, setSelectedMessages] = useState(new Set());
  
  // ============================================
  // TRANSLATE STATE (Independent)
  // ============================================
  const [translateLanguages, setTranslateLanguages] = useState([]);
  const [translateResults, setTranslateResults] = useState([]);
  const [translateProcessing, setTranslateProcessing] = useState(false);
  const [translateError, setTranslateError] = useState(null);
  const [translateRegenerations, setTranslateRegenerations] = useState({});

  // ============================================
  // GENERATE STATE (Independent)
  // ============================================
  const [generateLanguages, setGenerateLanguages] = useState([]);
  const [generateResults, setGenerateResults] = useState([]);
  const [generateProcessing, setGenerateProcessing] = useState(false);
  const [generateError, setGenerateError] = useState(null);
  const [generateRegenerations, setGenerateRegenerations] = useState({});

  // ============================================
  // CUSTOM PROMPT STATE (Independent)
  // ============================================
  const [customResults, setCustomResults] = useState([]);
  const [customProcessing, setCustomProcessing] = useState(false);
  const [customError, setCustomError] = useState(null);
  const [customRegenerations, setCustomRegenerations] = useState({});

  // ============================================
  // HISTORY STATE (Integrado)
  // ============================================
  const [historyItems, setHistoryItems] = useState([]);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);

  // ============================================
  // SAVED MESSAGES STATE (Knowledge Base)
  // ============================================
  const [savedMessages, setSavedMessages] = useState([]);
  const [savedMessagesLoading, setSavedMessagesLoading] = useState(false);
  const [savedMessagesLoaded, setSavedMessagesLoaded] = useState(false);
  const [isSavedMessagesOpen, setIsSavedMessagesOpen] = useState(false);

  // ============================================
  // UI STATE
  // ============================================
  const [activeDropdown, setActiveDropdown] = useState(null);

  // ============================================
  // HISTORY: Load from localStorage on mount
  // ============================================
  useEffect(() => {
    try {
      const stored = localStorage.getItem(HISTORY_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          setHistoryItems(parsed);
          console.log('[AIWorkspace] History loaded:', parsed.length, 'items');
        }
      }
    } catch (err) {
      console.error('[AIWorkspace] Error loading history:', err);
      setHistoryItems([]);
    }
  }, []);

  // ============================================
  // HISTORY: Save to localStorage
  // ============================================
  const saveHistoryToStorage = useCallback((items) => {
    try {
      localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(items));
      console.log('[AIWorkspace] History saved:', items.length, 'items');
    } catch (err) {
      console.error('[AIWorkspace] Error saving history:', err);
    }
  }, []);

  // ============================================
  // HISTORY: Add item(s) to history
  // ============================================
  const addToHistory = useCallback((results) => {
    if (!results) return;
    
    // Handle single result or array
    const resultsArray = Array.isArray(results) ? results : [results];
    if (resultsArray.length === 0) return;

    setHistoryItems(prev => {
      const existingIds = new Set(prev.map(item => item.id));
      const newItems = resultsArray
        .filter(r => r && r.id && !existingIds.has(r.id))
        .map(r => ({
          ...r,
          savedAt: new Date().toISOString(),
          isHistoryItem: true,
        }));

      if (newItems.length === 0) return prev;

      const updated = [...newItems, ...prev].slice(0, MAX_HISTORY_ITEMS);
      saveHistoryToStorage(updated);
      return updated;
    });
  }, [saveHistoryToStorage]);

  // ============================================
  // HISTORY: Remove from history
  // ============================================
  const removeFromHistory = useCallback((itemId) => {
    setHistoryItems(prev => {
      const updated = prev.filter(item => item.id !== itemId);
      saveHistoryToStorage(updated);
      return updated;
    });
  }, [saveHistoryToStorage]);

  // ============================================
  // HISTORY: Clear all history
  // ============================================
  const clearHistory = useCallback(() => {
    setHistoryItems([]);
    saveHistoryToStorage([]);
  }, [saveHistoryToStorage]);

  // ============================================
  // HISTORY: Toggle visibility (mutually exclusive with Saved Messages)
  // ============================================
  const toggleHistory = useCallback(() => {
    setIsHistoryOpen(prev => {
      const next = !prev;
      if (next) setIsSavedMessagesOpen(false);
      return next;
    });
  }, []);

  const openHistory = useCallback(() => {
    setIsSavedMessagesOpen(false);
    setIsHistoryOpen(true);
  }, []);

  const closeHistory = useCallback(() => {
    setIsHistoryOpen(false);
  }, []);

  // ============================================
  // SAVED MESSAGES: Load + Toggle (mutually exclusive with History)
  // ============================================
  const loadSavedMessages = useCallback(async () => {
    setSavedMessagesLoading(true);
    try {
      const res = await savedMessagesService.list();
      setSavedMessages(res?.items || []);
      setSavedMessagesLoaded(true);
    } catch (err) {
      console.error('[AIWorkspace] Saved messages load error:', err);
    } finally {
      setSavedMessagesLoading(false);
    }
  }, []);

  const toggleSavedMessages = useCallback(() => {
    setIsSavedMessagesOpen(prev => {
      const next = !prev;
      if (next) {
        setIsHistoryOpen(false);
        // Load on first open (refresh manually via reloadSavedMessages if needed)
        if (!savedMessagesLoaded) {
          loadSavedMessages();
        }
      }
      return next;
    });
  }, [savedMessagesLoaded, loadSavedMessages]);

  const openSavedMessages = useCallback(() => {
    setIsHistoryOpen(false);
    setIsSavedMessagesOpen(true);
    if (!savedMessagesLoaded) loadSavedMessages();
  }, [savedMessagesLoaded, loadSavedMessages]);

  const closeSavedMessages = useCallback(() => {
    setIsSavedMessagesOpen(false);
  }, []);

  const reloadSavedMessages = useCallback(() => {
    return loadSavedMessages();
  }, [loadSavedMessages]);

  // ============================================
  // DERIVED STATE
  // ============================================
  const hasTranslateResults = translateResults.length > 0;
  const hasGenerateResults = generateResults.length > 0;
  const hasCustomResults = customResults.length > 0;
  const hasAnyResults = hasTranslateResults || hasGenerateResults || hasCustomResults;
  const isAnyProcessing = translateProcessing || generateProcessing || customProcessing;
  const hasHistory = historyItems.length > 0;

  // ============================================
  // WORKSPACE TOGGLE
  // ============================================

  const toggleWorkspace = useCallback(() => {
    setIsWorkspaceOpen(prev => !prev);
  }, []);

  const openWorkspace = useCallback(() => {
    setIsWorkspaceOpen(true);
  }, []);

  const closeWorkspace = useCallback(() => {
    setIsWorkspaceOpen(false);
  }, []);

  // ============================================
  // MESSAGE SELECTION
  // ============================================

  const canSelectMessage = useCallback((message) => {
    if (selectedMessages.size >= MAX_SELECTED_MESSAGES) {
      return { allowed: false, reason: `Você pode selecionar até ${MAX_SELECTED_MESSAGES} mensagens` };
    }

    if (!message) {
      return { allowed: false, reason: 'Mensagem inválida' };
    }

    // WA Business format (flat: .type + .text + .media_url)
    if (typeof message.type === 'string' && !message.message) {
      if (message.type === 'text') {
        if (message.text) return { allowed: true };
        return { allowed: false, reason: 'Esta mensagem não contém texto' };
      }
      // Mídia (image/audio/video/document) — aceitar se tiver URL.
      if (['image', 'audio', 'video', 'document'].includes(message.type)) {
        if (message.media_url) return { allowed: true };
        return { allowed: false, reason: 'Mídia indisponível' };
      }
      // sticker, contact, location etc. caem aqui.
      return { allowed: false, reason: 'Tipo de mensagem não suportado pelo AI Workspace' };
    }

    // WA Personal / Evolution API format (nested .message object)
    const msgContent = message?.message || {};
    const hasBlockedType = BLOCKED_MESSAGE_TYPES.some(type => msgContent[type]);
    if (hasBlockedType) {
      return { allowed: false, reason: 'Stickers, contatos e localização não são suportados' };
    }

    const hasText = msgContent.conversation || msgContent.extendedTextMessage?.text;
    if (hasText) return { allowed: true };

    const mediaWithUrl = (
      msgContent.imageMessage?.url ||
      msgContent.audioMessage?.url ||
      msgContent.videoMessage?.url ||
      msgContent.documentMessage?.url
    );
    if (mediaWithUrl) return { allowed: true };

    return { allowed: false, reason: 'Esta mensagem não pode ser selecionada' };
  }, [selectedMessages.size]);

  const toggleMessageSelection = useCallback((msgId, message = null) => {
    setSelectedMessages(prev => {
      const newSet = new Set(prev);
      
      if (newSet.has(msgId)) {
        newSet.delete(msgId);
      } else {
        if (prev.size >= MAX_SELECTED_MESSAGES) {
          return prev;
        }
        
        if (message) {
          const { allowed } = canSelectMessage(message);
          if (!allowed) return prev;
        }
        
        newSet.add(msgId);
      }
      
      // Auto-open workspace when selecting messages
      if (newSet.size > 0) {
        setIsWorkspaceOpen(true);
      }
      
      return newSet;
    });
  }, [canSelectMessage]);

  const clearSelection = useCallback(() => {
    setSelectedMessages(new Set());
    // Also reset generate state when clearing selection
    setGenerateLanguages([]);
    setGenerateResults([]);
    setGenerateError(null);
    setGenerateRegenerations({});
  }, []);

  const getSelectedMessagesData = useCallback((allMessages) => {
    return allMessages.filter(msg => {
      const msgId = msg.id || msg.key?.id;
      return selectedMessages.has(msgId);
    });
  }, [selectedMessages]);

  // ============================================
  // LANGUAGE SELECTION
  // ============================================

  const toggleTranslateLanguage = useCallback((langCode) => {
    setTranslateLanguages(prev => {
      if (prev.includes(langCode)) {
        return prev.filter(l => l !== langCode);
      }
      if (prev.length >= MAX_LANGUAGES) return prev;
      return [...prev, langCode];
    });
  }, []);

  const toggleGenerateLanguage = useCallback((langCode) => {
    setGenerateLanguages(prev => {
      if (prev.includes(langCode)) {
        return prev.filter(l => l !== langCode);
      }
      if (prev.length >= MAX_LANGUAGES) return prev;
      return [...prev, langCode];
    });
  }, []);

  // ============================================
  // DROPDOWN CONTROL
  // ============================================

  const openDropdown = useCallback((type) => {
    setActiveDropdown(type);
  }, []);

  const closeDropdown = useCallback(() => {
    setActiveDropdown(null);
  }, []);

  const toggleDropdown = useCallback((type) => {
    setActiveDropdown(prev => prev === type ? null : type);
  }, []);

  // ============================================
  // EXECUTE TRANSLATE
  // ✅ Agora salva no histórico automaticamente
  // ============================================

  const executeTranslate = useCallback(async (inputText, apiCallback) => {
    if (!inputText?.trim() || translateLanguages.length === 0) return;

    setTranslateProcessing(true);
    setTranslateError(null);
    setActiveDropdown(null); // Close dropdown

    try {
      const results = await apiCallback({
        action: ACTION_TYPES.TRANSLATE,
        text: inputText.trim(),
        languages: translateLanguages,
      });

      const regenCounts = {};
      results.forEach(r => { regenCounts[r.id] = 0; });
      
      // Add new results to start (keep previous results)
      setTranslateResults(prev => [...results, ...prev]);
      setTranslateRegenerations(prev => ({ ...prev, ...regenCounts }));
      
      // ✅ SALVAR NO HISTÓRICO
      addToHistory(results);
      
      // Reset languages after successful execution
      setTranslateLanguages([]);
      
    } catch (err) {
      console.error('[AIWorkspace] Translate error:', err);
      setTranslateError(err.message || 'Erro ao traduzir');
    } finally {
      setTranslateProcessing(false);
    }
  }, [translateLanguages, addToHistory]);

  // ============================================
  // EXECUTE GENERATE
  // ✅ Agora salva no histórico automaticamente
  // ✅ Não precisa mais de seleção de idiomas - detecta automaticamente
  // ============================================

  const executeGenerate = useCallback(async (messageTexts, apiCallback, context = {}) => {
    if (!messageTexts?.length) return;

    setGenerateProcessing(true);
    setGenerateError(null);
    setActiveDropdown(null); // Close dropdown

    try {
      const results = await apiCallback({
        action: ACTION_TYPES.GENERATE,
        messages: messageTexts,
        languages: [], // Empty - will auto-detect
        context,
      });

      const regenCounts = {};
      results.forEach(r => { regenCounts[r.id] = 0; });
      
      // Add new results to start (keep previous results)
      setGenerateResults(prev => [...results, ...prev]);
      setGenerateRegenerations(prev => ({ ...prev, ...regenCounts }));
      
      // ✅ SALVAR NO HISTÓRICO
      addToHistory(results);
      
      // Clear selection after successful execution (no need to reset languages)
      setSelectedMessages(new Set());
      
    } catch (err) {
      console.error('[AIWorkspace] Generate error:', err);
      setGenerateError(err.message || 'Erro ao gerar resposta');
    } finally {
      setGenerateProcessing(false);
    }
  }, [addToHistory]);

  // ============================================
  // EXECUTE CUSTOM PROMPT
  // ✅ Agora salva no histórico automaticamente
  // ============================================

  const executeCustom = useCallback(async (prompt, apiCallback, context = {}) => {
    if (!prompt?.trim()) return;

    setCustomProcessing(true);
    setCustomError(null);

    try {
      const results = await apiCallback({
        action: ACTION_TYPES.CUSTOM,
        prompt: prompt.trim(),
        context,
      });

      const regenCounts = {};
      results.forEach(r => { regenCounts[r.id] = 0; });
      
      setCustomResults(prev => [...results, ...prev]); // Add to start
      setCustomRegenerations(prev => ({ ...prev, ...regenCounts }));
      
      // ✅ SALVAR NO HISTÓRICO
      addToHistory(results);
      
    } catch (err) {
      console.error('[AIWorkspace] Custom prompt error:', err);
      setCustomError(err.message || 'Erro ao processar prompt');
    } finally {
      setCustomProcessing(false);
    }
  }, [addToHistory]);

  // ============================================
  // REGENERATE
  // ============================================

  const regenerateResult = useCallback(async (resultId, actionType, apiCallback, params) => {
    // Get the right state based on action type
    let regenerations, setRegenerations, setResults, results;
    
    if (actionType === ACTION_TYPES.TRANSLATE) {
      regenerations = translateRegenerations;
      setRegenerations = setTranslateRegenerations;
      setResults = setTranslateResults;
      results = translateResults;
    } else if (actionType === ACTION_TYPES.GENERATE) {
      regenerations = generateRegenerations;
      setRegenerations = setGenerateRegenerations;
      setResults = setGenerateResults;
      results = generateResults;
    } else if (actionType === ACTION_TYPES.CUSTOM) {
      regenerations = customRegenerations;
      setRegenerations = setCustomRegenerations;
      setResults = setCustomResults;
      results = customResults;
    } else {
      return { success: false, reason: 'Tipo de ação desconhecido' };
    }

    const currentCount = regenerations[resultId] || 0;
    if (currentCount >= MAX_REGENERATIONS) {
      return { success: false, reason: 'Limite de regenerações atingido' };
    }

    const resultToRegen = results.find(r => r.id === resultId);
    if (!resultToRegen) return { success: false, reason: 'Resultado não encontrado' };

    try {
      // Build callback params based on action type
      let callbackParams = {
        ...params,
        action: actionType,
        isRegeneration: true,
      };
      
      // For custom prompts, don't pass languages array
      if (actionType !== ACTION_TYPES.CUSTOM) {
        callbackParams.languages = [resultToRegen.language.code];
      }

      const newResults = await apiCallback(callbackParams);

      if (newResults && newResults.length > 0) {
        const newResult = newResults[0];
        
        setResults(prev => prev.map(r => 
          r.id === resultId ? { ...newResult, id: resultId } : r
        ));
        
        setRegenerations(prev => ({
          ...prev,
          [resultId]: (prev[resultId] || 0) + 1,
        }));

        return { success: true };
      }
    } catch (err) {
      console.error('[AIWorkspace] Regenerate error:', err);
      return { success: false, reason: err.message };
    }

    return { success: false, reason: 'Erro desconhecido' };
  }, [translateRegenerations, generateRegenerations, customRegenerations, translateResults, generateResults, customResults]);

  const getRemainingRegenerations = useCallback((resultId, actionType) => {
    let regenerations;
    if (actionType === ACTION_TYPES.TRANSLATE) {
      regenerations = translateRegenerations;
    } else if (actionType === ACTION_TYPES.GENERATE) {
      regenerations = generateRegenerations;
    } else if (actionType === ACTION_TYPES.CUSTOM) {
      regenerations = customRegenerations;
    } else {
      return 0;
    }
    const used = regenerations[resultId] || 0;
    return MAX_REGENERATIONS - used;
  }, [translateRegenerations, generateRegenerations, customRegenerations]);

  // ============================================
  // RESULT MANAGEMENT
  // ============================================

  const removeTranslateResult = useCallback((resultId) => {
    setTranslateResults(prev => prev.filter(r => r.id !== resultId));
    setTranslateRegenerations(prev => {
      const newRegens = { ...prev };
      delete newRegens[resultId];
      return newRegens;
    });
  }, []);

  const removeGenerateResult = useCallback((resultId) => {
    setGenerateResults(prev => prev.filter(r => r.id !== resultId));
    setGenerateRegenerations(prev => {
      const newRegens = { ...prev };
      delete newRegens[resultId];
      return newRegens;
    });
  }, []);

  const removeCustomResult = useCallback((resultId) => {
    setCustomResults(prev => prev.filter(r => r.id !== resultId));
    setCustomRegenerations(prev => {
      const newRegens = { ...prev };
      delete newRegens[resultId];
      return newRegens;
    });
  }, []);

  const clearTranslateResults = useCallback(() => {
    setTranslateResults([]);
    setTranslateRegenerations({});
    setTranslateError(null);
  }, []);

  const clearGenerateResults = useCallback(() => {
    setGenerateResults([]);
    setGenerateRegenerations({});
    setGenerateError(null);
  }, []);

  const clearCustomResults = useCallback(() => {
    setCustomResults([]);
    setCustomRegenerations({});
    setCustomError(null);
  }, []);

  // Full reset
  const resetWorkspace = useCallback(() => {
    setSelectedMessages(new Set());
    setTranslateLanguages([]);
    setTranslateResults([]);
    setTranslateError(null);
    setTranslateRegenerations({});
    setGenerateLanguages([]);
    setGenerateResults([]);
    setGenerateError(null);
    setGenerateRegenerations({});
    setCustomResults([]);
    setCustomError(null);
    setCustomRegenerations({});
    setActiveDropdown(null);
    // Nota: NÃO limpa o histórico no reset
  }, []);

  // ============================================
  // RETURN
  // ============================================

  return {
    // Workspace visibility
    isWorkspaceOpen,
    toggleWorkspace,
    openWorkspace,
    closeWorkspace,

    // Selection
    selectedMessages,
    toggleMessageSelection,
    clearSelection,
    getSelectedMessagesData,
    canSelectMessage,
    maxSelectedMessages: MAX_SELECTED_MESSAGES,

    // Derived state
    hasAnyResults,
    isAnyProcessing,

    // Dropdown
    activeDropdown,
    openDropdown,
    closeDropdown,
    toggleDropdown,

    // Translate
    translateLanguages,
    toggleTranslateLanguage,
    translateResults,
    translateProcessing,
    translateError,
    executeTranslate,
    removeTranslateResult,
    clearTranslateResults,
    hasTranslateResults,

    // Generate
    generateLanguages,
    toggleGenerateLanguage,
    generateResults,
    generateProcessing,
    generateError,
    executeGenerate,
    removeGenerateResult,
    clearGenerateResults,
    hasGenerateResults,

    // Custom Prompt
    customResults,
    customProcessing,
    customError,
    executeCustom,
    removeCustomResult,
    clearCustomResults,
    hasCustomResults,

    // ✅ HISTORY (agora integrado)
    historyItems,
    isHistoryOpen,
    hasHistory,
    toggleHistory,
    openHistory,
    closeHistory,
    addToHistory,
    removeFromHistory,
    clearHistory,
    maxHistoryItems: MAX_HISTORY_ITEMS,

    // ✅ SAVED MESSAGES (Knowledge Base)
    savedMessages,
    savedMessagesLoading,
    isSavedMessagesOpen,
    toggleSavedMessages,
    openSavedMessages,
    closeSavedMessages,
    reloadSavedMessages,

    // Regeneration
    regenerateResult,
    getRemainingRegenerations,
    maxRegenerations: MAX_REGENERATIONS,

    // Reset
    resetWorkspace,
  };
};

export default useAIWorkspace;